/**
 * RendPleX Scenario Analyzer 2.0 — P3D Acquisition Decision Summary.
 *
 * Presentation-only. Consumes existing P3A/P3B/P3C service outputs
 * via POST /api/scenario/decision-summary. Zero new financial math.
 *
 * The user explicitly picks a `summary_source`:
 *   - offer_price        (P3A)
 *   - financing_strategy (P3B, one of 5 strategies)
 *   - constraints        (P3C)
 * No source is silently inferred.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import {
  ClipboardList, Loader2, CheckCircle2, AlertTriangle,
  ArrowRight, Zap, Lock,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STRATEGY_LABEL = {
  current:     'financing.strategyCurrent',
  more_equity: 'financing.strategyMoreEquity',
  better_rate: 'financing.strategyBetterRate',
  lower_price: 'financing.strategyLowerPrice',
  optimized:   'financing.strategyOptimized',
};

const BINDING_I18N = {
  max_cash_available:        'constraints.bindingMaxCash',
  max_purchase_price:        'constraints.bindingMaxPrice',
  max_down_payment_percent:  'constraints.bindingMaxDp',
  min_down_payment_percent:  'constraints.bindingMinDp',
  scenario_bound_price_min:  'constraints.bindingScenarioFloor',
  locked_purchase_price:     'constraints.bindingLockedPrice',
  locked_interest_rate:      'constraints.bindingLockedRate',
  locked_rent:               'constraints.bindingLockedRent',
  locked_down_payment:       'constraints.bindingLockedDp',
};

const LEVER_I18N = {
  purchase_price: 'summary.leverPurchasePrice',
  down_payment:   'summary.leverDownPayment',
  interest_rate:  'summary.leverInterestRate',
  rent:           'summary.leverRent',
};

const CHANGE_FIELD_I18N = {
  purchase_price:       'summary.fieldPurchasePrice',
  down_payment_percent: 'summary.fieldDpPct',
  interest_rate:        'summary.fieldRate',
  annual_income:        'summary.fieldRent',
};

const fmtMoney = (n) => (n === null || n === undefined || Number.isNaN(n))
  ? '—' : `$${Math.round(n).toLocaleString()}`;
const fmtPct = (n, dec = 2) => (n === null || n === undefined || Number.isNaN(n))
  ? '—' : `${n.toFixed(dec)}%`;
const fmtRatio = (n, dec = 2) => (n === null || n === undefined || Number.isNaN(n))
  ? '—' : n.toFixed(dec);

const fmtChangeValue = (field, value) => {
  if (value === null || value === undefined) return '—';
  if (field === 'purchase_price' || field === 'annual_income') return fmtMoney(value);
  return fmtPct(value, 2);
};

const MetricPair = ({ label, baselineVal, modeledVal, formatter }) => (
  <div className="flex items-baseline justify-between gap-2 py-1 border-b border-slate-100 dark:border-slate-800 last:border-b-0">
    <span className="text-[11px] uppercase tracking-wider text-slate-500 dark:text-slate-400">{label}</span>
    <span className="font-mono tabular-nums text-xs">
      <span className="text-slate-500 dark:text-slate-400">{formatter(baselineVal)}</span>
      {modeledVal !== undefined && modeledVal !== null && (
        <>
          <ArrowRight className="inline w-3 h-3 mx-1 text-slate-400" />
          <span className="text-slate-900 dark:text-slate-100 font-semibold">{formatter(modeledVal)}</span>
        </>
      )}
    </span>
  </div>
);

const DecisionSummaryTab = ({ scenario, onApply }) => {
  const { t } = useTranslation();
  const [source, setSource] = useState('constraints');
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  // Source-specific inputs
  const [opMetric, setOpMetric] = useState('monthly_cash_flow');
  const [opTarget, setOpTarget] = useState(500);
  const [strategyId, setStrategyId] = useState('optimized');
  const [strategyTarget, setStrategyTarget] = useState(500);
  const [cMcf, setCMcf] = useState(500);
  const [cDscr, setCDscr] = useState(1.20);
  const [cMaxCash, setCMaxCash] = useState(225_000);

  const run = async () => {
    setLoading(true); setError(null); setData(null);
    try {
      let body = { scenario, summary_source: source, locked: [] };
      if (source === 'offer_price') {
        body.target_metric = opMetric;
        body.target_value = Number(opTarget);
      } else if (source === 'financing_strategy') {
        body.strategy_id = strategyId;
        body.target_metric = 'monthly_cash_flow';
        body.target_value = Number(strategyTarget);
      } else {
        body.constraints = {
          min_monthly_cash_flow: Number(cMcf),
          min_dscr: Number(cDscr),
          max_cash_available: Number(cMaxCash),
        };
      }
      const resp = await axios.post(
        `${BACKEND_URL}/api/scenario/decision-summary`,
        body,
        { withCredentials: true }
      );
      setData(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('summary.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const apply = () => {
    if (!data?.modeled?.scenario) return;
    onApply({
      purchase_price: data.modeled.scenario.purchase_price,
      annual_income: data.modeled.scenario.annual_income,
      down_payment_percent: data.modeled.scenario.down_payment_percent,
      interest_rate: data.modeled.scenario.interest_rate,
    });
    toast.success(t('summary.appliedToast'));
  };

  const outcome = data?.outcome;
  const isAchievable = outcome?.status === 'achievable';
  const isImpossible = outcome?.status === 'impossible';
  const isNotAchievable = outcome?.status === 'not_achievable';
  const isNoHardFeasible = isNotAchievable && outcome?.reason_code === 'no_hard_feasible_scenario';
  const isInvalid = outcome?.status === 'invalid_constraints';
  const isNotApplicable = outcome?.status === 'not_applicable';
  const isUnavailable = outcome?.status === 'unavailable';

  return (
    <div
      data-testid="decision-summary-tab"
      role="region"
      aria-live="polite"
      aria-atomic="false"
      className="space-y-4"
    >
      {/* Source picker */}
      <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-4 space-y-3">
        <label className="text-xs uppercase tracking-wider font-semibold text-slate-700 dark:text-slate-300">
          {t('summary.sourcePickerLabel')}
        </label>
        <div className="flex flex-wrap gap-2">
          {['offer_price', 'financing_strategy', 'constraints'].map((s) => (
            <button
              key={s}
              type="button"
              data-testid={`summary-source-${s}`}
              aria-pressed={source === s}
              onClick={() => { setSource(s); setData(null); }}
              className={`px-3 py-1.5 text-xs rounded-md border transition-colors ${
                source === s
                  ? 'border-indigo-500 bg-indigo-50 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300'
                  : 'border-slate-300 dark:border-slate-700 text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-slate-100'
              }`}
            >
              {t(`summary.source_${s}`)}
            </button>
          ))}
        </div>

        {/* Source-specific inputs */}
        {source === 'offer_price' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <select
              data-testid="summary-op-metric"
              value={opMetric}
              onChange={(e) => setOpMetric(e.target.value)}
              className="h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0F1419] px-2"
            >
              <option value="monthly_cash_flow">{t('offerPrice.metricMonthlyCashFlow')}</option>
              <option value="dscr">{t('offerPrice.metricDscr')}</option>
              <option value="cap_rate">{t('offerPrice.metricCapRate')}</option>
              <option value="cash_on_cash">{t('offerPrice.metricCashOnCash')}</option>
            </select>
            <input
              data-testid="summary-op-target"
              type="number"
              step={1}
              value={opTarget}
              onChange={(e) => setOpTarget(e.target.value === '' ? 0 : Number(e.target.value))}
              className="h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0F1419] px-2 font-mono tabular-nums"
              placeholder={t('offerPrice.targetValue')}
            />
          </div>
        )}
        {source === 'financing_strategy' && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs">
            <select
              data-testid="summary-strategy"
              value={strategyId}
              onChange={(e) => setStrategyId(e.target.value)}
              className="h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0F1419] px-2"
            >
              {Object.keys(STRATEGY_LABEL).map((sid) => (
                <option key={sid} value={sid}>{t(STRATEGY_LABEL[sid])}</option>
              ))}
            </select>
            <input
              data-testid="summary-strategy-target"
              type="number"
              step={50}
              value={strategyTarget}
              onChange={(e) => setStrategyTarget(e.target.value === '' ? 0 : Number(e.target.value))}
              className="h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0F1419] px-2 font-mono tabular-nums"
              placeholder={t('financing.targetForOptimizations')}
            />
          </div>
        )}
        {source === 'constraints' && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 text-xs">
            <input data-testid="summary-c-mcf"   type="number" step={50}  value={cMcf}     onChange={(e) => setCMcf(Number(e.target.value || 0))}     placeholder={t('constraints.minMonthlyCashFlow')} className="h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0F1419] px-2 font-mono tabular-nums" />
            <input data-testid="summary-c-dscr"  type="number" step={0.05} value={cDscr}    onChange={(e) => setCDscr(Number(e.target.value || 0))}    placeholder={t('constraints.minDscr')} className="h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0F1419] px-2 font-mono tabular-nums" />
            <input data-testid="summary-c-cash"  type="number" step={1000} value={cMaxCash} onChange={(e) => setCMaxCash(Number(e.target.value || 0))} placeholder={t('constraints.maxCashAvailable')} className="h-9 rounded-md border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0F1419] px-2 font-mono tabular-nums" />
          </div>
        )}

        <Button
          data-testid="summary-run-btn"
          onClick={run}
          disabled={loading}
          className="h-9 bg-indigo-600 hover:bg-indigo-700 text-white text-xs"
        >
          {loading ? (<><Loader2 className="w-3 h-3 mr-1.5 animate-spin" />{t('summary.building')}</>)
                   : (<><ClipboardList className="w-3 h-3 mr-1.5" />{t('summary.buildSummary')}</>)}
        </Button>
      </div>

      {error && (
        <p data-testid="summary-error" className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {/* Empty state */}
      {!data && !error && !loading && (
        <div data-testid="summary-empty-state"
             className="rounded-lg border border-dashed border-slate-300 dark:border-slate-700 p-6 text-center">
          <ClipboardList className="w-6 h-6 text-slate-400 mx-auto mb-2" />
          <p className="text-sm text-slate-700 dark:text-slate-300">{t('summary.emptyTitle')}</p>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 mt-1">{t('summary.emptyHint')}</p>
        </div>
      )}

      {data && (
        <>
          {/* Outcome banner */}
          <div className="rounded-lg border p-3 flex items-start gap-2"
               data-testid={`summary-outcome-${outcome.status || 'unknown'}`}
               data-reason-code={outcome.reason_code || ''}>
            {(isAchievable) ? (
              <>
                <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800 dark:text-emerald-300">{t('summary.outcomeAchievable')}</p>
                </div>
              </>
            ) : isImpossible ? (
              <>
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800 dark:text-amber-300">{t('summary.outcomeOfferImpossible')}</p>
                </div>
              </>
            ) : isNoHardFeasible ? (
              <>
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800 dark:text-red-300">{t('summary.outcomeNoHardFeasible')}</p>
                </div>
              </>
            ) : isNotAchievable ? (
              <>
                <AlertTriangle className="w-5 h-5 text-amber-600 dark:text-amber-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-amber-800 dark:text-amber-300">{t('summary.outcomeNotAchievable')}</p>
                </div>
              </>
            ) : isInvalid ? (
              <>
                <AlertTriangle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-red-800 dark:text-red-300">{t('summary.outcomeInvalid')}</p>
                </div>
              </>
            ) : isNotApplicable || isUnavailable ? (
              <>
                <AlertTriangle className="w-5 h-5 text-slate-500 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-slate-800 dark:text-slate-300">{t('summary.outcomeNotApplicable')}</p>
                </div>
              </>
            ) : (
              <>
                <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-emerald-800 dark:text-emerald-300">{t('summary.outcomeAchievable')}</p>
                </div>
              </>
            )}
          </div>

          {/* Current + Modeled snapshot pair */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-4">
              <h3 className="font-primary font-bold text-sm text-slate-900 dark:text-slate-100 uppercase tracking-wider mb-2">
                {t('summary.currentDeal')}
              </h3>
              <MetricPair label={t('summary.fieldPurchasePrice')}
                          baselineVal={data.baseline.scenario.purchase_price}
                          formatter={fmtMoney} />
              <MetricPair label={t('summary.fieldDpPct')}
                          baselineVal={data.baseline.scenario.down_payment_percent}
                          formatter={(n) => fmtPct(n, 1)} />
              <MetricPair label={t('summary.fieldRate')}
                          baselineVal={data.baseline.scenario.interest_rate}
                          formatter={(n) => fmtPct(n, 2)} />
              <MetricPair label={t('constraints.metricMcf')}
                          baselineVal={data.baseline.metrics?.monthly_cash_flow}
                          formatter={fmtMoney} />
              <MetricPair label={t('constraints.metricDscr')}
                          baselineVal={data.baseline.metrics?.dscr}
                          formatter={(n) => fmtRatio(n, 2)} />
              <MetricPair label={t('constraints.metricCap')}
                          baselineVal={data.baseline.metrics?.cap_rate}
                          formatter={(n) => fmtPct(n, 2)} />
              <MetricPair label={t('constraints.metricCoc')}
                          baselineVal={data.baseline.metrics?.cash_on_cash}
                          formatter={(n) => fmtPct(n, 2)} />
              <MetricPair label={t('constraints.scenarioCash')}
                          baselineVal={data.baseline.cash_required}
                          formatter={fmtMoney} />
            </div>
            <div data-testid="summary-modeled-block"
                 className={`rounded-lg border p-4 ${
              data.modeled
                ? 'border-indigo-300 dark:border-indigo-800 bg-indigo-50/60 dark:bg-indigo-950/20'
                : 'border-slate-200 dark:border-slate-800 bg-slate-50/60 dark:bg-slate-900/40'
            }`}>
              <h3 className="font-primary font-bold text-sm text-slate-900 dark:text-slate-100 uppercase tracking-wider mb-2">
                {t('summary.modeledScenario')}
              </h3>
              {data.modeled ? (
                <>
                  <MetricPair label={t('summary.fieldPurchasePrice')}
                              baselineVal={data.baseline.scenario.purchase_price}
                              modeledVal={data.modeled.scenario.purchase_price}
                              formatter={fmtMoney} />
                  <MetricPair label={t('summary.fieldDpPct')}
                              baselineVal={data.baseline.scenario.down_payment_percent}
                              modeledVal={data.modeled.scenario.down_payment_percent}
                              formatter={(n) => fmtPct(n, 1)} />
                  <MetricPair label={t('summary.fieldRate')}
                              baselineVal={data.baseline.scenario.interest_rate}
                              modeledVal={data.modeled.scenario.interest_rate}
                              formatter={(n) => fmtPct(n, 2)} />
                  <MetricPair label={t('constraints.metricMcf')}
                              baselineVal={data.baseline.metrics?.monthly_cash_flow}
                              modeledVal={data.modeled.metrics?.monthly_cash_flow}
                              formatter={fmtMoney} />
                  <MetricPair label={t('constraints.metricDscr')}
                              baselineVal={data.baseline.metrics?.dscr}
                              modeledVal={data.modeled.metrics?.dscr}
                              formatter={(n) => fmtRatio(n, 2)} />
                  <MetricPair label={t('constraints.scenarioCash')}
                              baselineVal={data.baseline.cash_required}
                              modeledVal={data.modeled.cash_required}
                              formatter={fmtMoney} />
                  <div className="mt-3">
                    <Button
                      data-testid="summary-apply-btn"
                      size="sm"
                      variant="outline"
                      className="border-indigo-400 dark:border-indigo-700 text-xs"
                      onClick={apply}
                    >
                      {t('summary.applyToScenario')}
                    </Button>
                  </div>
                </>
              ) : (
                <p className="text-xs text-slate-500 dark:text-slate-400 italic">
                  {t('summary.noModeledScenario')}
                </p>
              )}
            </div>
          </div>

          {/* What Changed */}
          {data.changes.length > 0 && (
            <div data-testid="summary-changes-block"
                 className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-4">
              <h3 className="font-primary font-bold text-sm text-slate-900 dark:text-slate-100 uppercase tracking-wider mb-2">
                {t('summary.whatChanged')}
              </h3>
              <div className="space-y-1">
                {data.changes.map((c) => (
                  <div key={c.field}
                       data-testid={`summary-change-${c.field}`}
                       className="flex items-baseline justify-between gap-2 py-1 text-xs">
                    <span className="text-slate-600 dark:text-slate-400">{t(CHANGE_FIELD_I18N[c.field] || c.field)}</span>
                    <span className="font-mono tabular-nums">
                      <span className="text-slate-500 dark:text-slate-400">{fmtChangeValue(c.field, c.baseline)}</span>
                      <ArrowRight className="inline w-3 h-3 mx-1 text-slate-400" />
                      <span className="text-slate-900 dark:text-slate-100 font-semibold">{fmtChangeValue(c.field, c.modeled)}</span>
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Requirement Status (P3C only) */}
          {(data.requirement_results?.length > 0 || data.unmet_requirements?.length > 0) && (
            <div data-testid="summary-requirements-block"
                 className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-4">
              <h3 className="font-primary font-bold text-sm text-slate-900 dark:text-slate-100 uppercase tracking-wider mb-2">
                {isAchievable ? t('summary.allRequirementsSatisfied') : t('summary.unmetRequirements')}
              </h3>
              <div className="space-y-1">
                {(isAchievable ? data.requirement_results : data.unmet_requirements).map((r) => {
                  const Icon = r.satisfied ? CheckCircle2 : AlertTriangle;
                  const cls = r.satisfied ? 'text-emerald-600 dark:text-emerald-400' : 'text-amber-600 dark:text-amber-400';
                  return (
                    <div key={r.id}
                         data-testid={`summary-req-${r.id}`}
                         className="flex items-baseline justify-between gap-2 py-1 text-xs">
                      <span className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300">
                        <Icon className={`w-3.5 h-3.5 ${cls}`} />
                        {t(`summary.req_${r.id}`) || r.id}
                      </span>
                      <span className="font-mono tabular-nums text-slate-600 dark:text-slate-400">
                        {t('constraints.requiredLabel')}: {r.required} · {t('constraints.achievedLabel')}:
                        <span className={`ml-1 ${cls}`}>{r.achieved === null ? '—' : r.achieved}</span>
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          )}

          {/* Biggest Modeled Lever */}
          {data.biggest_lever && (
            <div data-testid="summary-biggest-lever"
                 className="rounded-lg border border-indigo-200 dark:border-indigo-900 bg-indigo-50/60 dark:bg-indigo-950/20 p-3 flex items-center gap-2">
              <Zap className="w-4 h-4 text-indigo-600 dark:text-indigo-400 flex-shrink-0" />
              <div>
                <p className="text-xs font-semibold text-indigo-800 dark:text-indigo-300">
                  {t('summary.biggestLever')}: {t(LEVER_I18N[data.biggest_lever.id] || data.biggest_lever.id)}
                </p>
                <p className="text-[10px] text-indigo-700 dark:text-indigo-400 italic">
                  {t('summary.biggestLeverSource')}
                </p>
              </div>
            </div>
          )}

          {/* Binding Constraints (P3C only) */}
          {data.binding_constraints?.length > 0 && (
            <div data-testid="summary-binding-block"
                 className="rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50/60 dark:bg-amber-950/20 p-3">
              <p className="text-xs font-semibold text-amber-800 dark:text-amber-300 uppercase tracking-wider mb-1">
                {t('summary.limitingConstraint')}
              </p>
              <ul className="text-xs text-amber-800 dark:text-amber-200 space-y-1">
                {data.binding_constraints.map((b) => (
                  <li key={b} data-testid={`summary-binding-${b}`} className="flex items-center gap-1.5">
                    <Lock className="w-3 h-3" />
                    {t(BINDING_I18N[b] || 'constraints.bindingGeneric', { code: b })}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Offer Price context */}
          {data.offer_price_context && (
            <div data-testid="summary-offer-context"
                 className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-3">
              <p className="text-xs font-semibold text-slate-800 dark:text-slate-300 uppercase tracking-wider mb-1">
                {t('summary.offerContextTitle')}
              </p>
              <p className="text-[11px] text-slate-600 dark:text-slate-400 italic">
                {t('summary.offerContextSubtitle')}
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs mt-2">
                <div>
                  <p className="text-[10px] uppercase text-slate-500 dark:text-slate-400">{t('offerPrice.askingPrice')}</p>
                  <p className="font-mono tabular-nums font-semibold">{fmtMoney(data.offer_price_context.asking_price)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-slate-500 dark:text-slate-400">{t('offerPrice.targetOfferPrice')}</p>
                  <p className="font-mono tabular-nums font-semibold">{fmtMoney(data.offer_price_context.offer_price)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-slate-500 dark:text-slate-400">{t('offerPrice.requiredReduction')}</p>
                  <p className="font-mono tabular-nums font-semibold">{fmtMoney(data.offer_price_context.required_reduction_abs)}</p>
                </div>
                <div>
                  <p className="text-[10px] uppercase text-slate-500 dark:text-slate-400">{t('offerPrice.targetResult')}</p>
                  <p className="font-mono tabular-nums font-semibold">{data.offer_price_context.achieved_value === null ? '—' : data.offer_price_context.achieved_value}</p>
                </div>
              </div>
            </div>
          )}

          {/* Financing context */}
          {data.financing_context && (
            <div data-testid="summary-financing-context"
                 className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-[#0F1419] p-3">
              <p className="text-xs font-semibold text-slate-800 dark:text-slate-300 uppercase tracking-wider mb-1">
                {t('summary.financingContextTitle')}: <span className="normal-case">{t(STRATEGY_LABEL[data.financing_context.strategy_id] || data.financing_context.strategy_id)}</span>
              </p>
              {data.financing_context.strategy_id === 'better_rate' && (
                <p className="text-[10px] text-slate-500 dark:text-slate-400 italic">
                  {t('financing.betterRateGuardrail')}
                </p>
              )}
            </div>
          )}

          <p className="text-[11px] text-slate-500 dark:text-slate-400 italic">
            {t('summary.deterministicNote')}
          </p>
        </>
      )}
    </div>
  );
};

export default DecisionSummaryTab;
